import React, { useState } from 'react';

import { EvalContent } from '../page-content/eval-content';
import {
  moveOnToNextPage,
  setStage,
  showConfetti,
  submitResponse
} from '../redux/actions/ui';
import {
  getCorrectElement,
  getEvalReviewMessage
} from '../components/side-bar';

import {
  Panel,
  setSideBarCorrect,
  setSideBarIncorrect,
  setSideBarInfo,
  setSideBarOpenPanel,
  setSideBarSecondTry
} from '../sidebar-state';

import { ProblemStage, EvalData, SelfEvalComp, ITSEventType } from 'its-pages';
import { SubmitBar } from '../components/submit-bar';
import { useAppDispatch, useAppSelector } from '../redux/hooks';

const next = 'Next';
const nextQuestion = 'Next Question';
const submit = 'Submit';
const buttonTextMap: { [key in ProblemStage]: string } = {
  [ProblemStage.ReadTranscript]: next,
  [ProblemStage.CollectInput]: submit,
  [ProblemStage.Evaluate]: submit,
  [ProblemStage.SecondTry]: submit,
  [ProblemStage.ShowResult]: nextQuestion
};
const enterText = 'Enter your response';
const enterEvaluation = 'Enter your self evaluation';
const rewrite = 'Rewrite your response';
const disabledButtonTextMap: { [key in ProblemStage]: string | undefined } = {
  [ProblemStage.ReadTranscript]: undefined,
  [ProblemStage.CollectInput]: enterText,
  [ProblemStage.Evaluate]: enterEvaluation,
  [ProblemStage.SecondTry]: rewrite,
  [ProblemStage.ShowResult]: undefined
};

interface ComponentProps {
  content: EvalContent;
}

/**
 * Self Evaluation type questions are when the user types a response to a dialog situation.
 * They must evaluate whether certain tags are included or excluded in their response and adjust
 * their response to meet those criteria.
 * 
 * @param props 
 * @returns 
 */
export const SelfEval = (props: ComponentProps) => {
  const pageData = props.content.pageData as EvalData;

  const correctAnswerStreak = useAppSelector((state) => state.progression.correctAnswerStreak);
  const stage = useAppSelector((state) => state.ui.stage);
  const dispatch = useAppDispatch();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [secondTry, setSecondTry] = useState(false);
  const [firstTryScore, setFirstTryScore] = useState(0);
  const showResult = stage === ProblemStage.ShowResult;

  const onSubmit = () => {
    const score = pageData.score;

    const isCorrect = score >= 0.9;

    const gotoShowResults = () => {
      dispatch(submitResponse());
      if (!isCorrect) {
        pageData.endReevaluation();
      }
      setSideBarInfo(<p>Thank you for your response.</p>);
      dispatch(setStage(ProblemStage.ShowResult));
    };

    const gotoCollectInput = () => {
      setDisableSubmit(true);
      dispatch(setStage(ProblemStage.CollectInput));
      pageData.startEvaluation();
    };

    const gotoEvaluate = () => {
      setDisableSubmit(true);
      dispatch(setStage(ProblemStage.Evaluate));
      pageData.endEvaluation();
    };

    const gotoSecondTry = () => {
      setDisableSubmit(true);
      setFirstTryScore(score);
      dispatch(setStage(ProblemStage.SecondTry));
      setSideBarSecondTry(props.content.getSecondTryHint());
      setSecondTry(true);
      pageData.recordEvent(ITSEventType.firstTryWrong);
      pageData.startReevaluation();
    };

    if (showResult) {
      dispatch(moveOnToNextPage());
      setDisableSubmit(true);
    } else if (isCorrect) {
      gotoShowResults();
    } else if (stage === ProblemStage.ReadTranscript) {
      gotoCollectInput();
    } else if (stage === ProblemStage.CollectInput) {
      gotoEvaluate();
    } else if (stage === ProblemStage.Evaluate) {
      gotoSecondTry();
    } else {
      gotoShowResults();
    }
  };

  const onReviewed = () => {
    if (showResult) {
      setSideBarOpenPanel(Panel.None);
    }
  };

  const onChange = () => {
    switch (stage) {
      case ProblemStage.CollectInput:
      case ProblemStage.SecondTry: {
        setDisableSubmit(false);
        break;
      }
      case ProblemStage.Evaluate: {
        const { evalAnswers, tagsToAvoid, tags } = pageData;
        const evalLength = Object.keys(evalAnswers).length;
        const totalTags =
          Object.keys(tagsToAvoid).length + Object.keys(tags).length;

        if (evalLength >= totalTags) {
          setDisableSubmit(false);
        }
      }
    }
  };

  return (
    <div className='Problem'>
      <SelfEvalComp
        pageData={pageData}
        stage={stage}
        onReviewed={onReviewed}
        onChange={onChange}
        showSecondResponse={secondTry}
      />
      <SubmitBar
        onSubmit={onSubmit}
        disableSubmit={disableSubmit}
        buttonText={buttonTextMap[stage]}
        disabledTip={disabledButtonTextMap[stage]}
        buttonClass={'SubmitBarButton'}
      />
    </div>
  );
};
